import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

export const SUPPORTED_LANGS = ['de', 'en', 'es'] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

const loaders: Record<SupportedLang, () => Promise<{ default: Record<string, unknown> }>> = {
  de: () => import('./locales/de.json'),
  en: () => import('./locales/en.json'),
  es: () => import('./locales/es.json'),
};

const detect = (): SupportedLang => {
  try {
    const stored = localStorage.getItem('i18nextLng');
    if (stored) {
      const match = SUPPORTED_LANGS.find((l) => stored.toLowerCase().startsWith(l));
      if (match) return match;
    }
  } catch {
    /* localStorage unavailable */
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language : 'de';
  return SUPPORTED_LANGS.find((l) => nav.toLowerCase().startsWith(l)) ?? 'de';
};

const initialLng = detect();

const loadLanguage = async (lng: SupportedLang) => {
  if (i18n.hasResourceBundle(lng, 'translation')) return;
  const mod = await loaders[lng]();
  i18n.addResourceBundle(lng, 'translation', mod.default, true, true);
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {},
    lng: initialLng,
    fallbackLng: 'de',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    // Re-render components once a lazily loaded bundle is added to the store
    react: {
      bindI18nStore: 'added',
    },
  });

// Load the active language bundle, then keep loading on demand.
export const i18nReady = loadLanguage(initialLng);
i18n.on('languageChanged', (lng) => {
  const match = SUPPORTED_LANGS.find((l) => lng?.toLowerCase().startsWith(l));
  if (match) void loadLanguage(match);
});

/** Load the target bundle first, then switch — avoids a flash of German text. */
export const changeAppLanguage = async (lng: string) => {
  const match = SUPPORTED_LANGS.find((l) => lng?.toLowerCase().startsWith(l));
  if (match) await loadLanguage(match);
  await i18n.changeLanguage(lng);
};

export default i18n;
